<template>
  <TransactionDetailsModal
    v-if="detailsModalVisible"
    :transaction="detailsModalTransaction"
    @closeModal="detailsModalVisible = false"
  />
  <PaymentModal
    v-if="paymentModalVisible"
    :transactions="selectedTransactions"
    @deleteTransaction="onDeleteSelectedTransaction"
    @closeModal="onPaymentModalClose"
  />
  <div class="financials-table" data-cy="financials-table">
    <div
      class="grid-control__container"
      data-cy="financials-table-control-container"
    >
      <p class="section__title--text" data-cy="financials-table-title">
        Transaction Detail
      </p>
      <div class="pull-right">
        <Button
          v-if="!main.isEmulator || allowEmulationPayments"
          theme-color="dark"
          type="button"
          :disabled="selectedTransactions.length === 0"
          @click="paymentModalVisible = true"
          data-cy="pay-selected-invoices"
          >Pay Selected Invoices Online</Button
        >
        <ClearFiltersButton @clearFiltersClicked="clearFilters" />
        <Button
          type="button"
          theme-color="primary"
          @click="downloadFormW9"
          data-cy="download-form-w9"
          >Download Form W9</Button
        >
        <LoadingButton
          theme-color="primary"
          type="button"
          :loading="exporting"
          @click="exportAllExcelAsync(true)"
          data-cy="export-filtered-financials-excel"
          >Export Filtered Data To Excel</LoadingButton
        >
        <LoadingButton
          theme-color="primary"
          type="button"
          :loading="exporting"
          @click="exportAllExcelAsync(false)"
          data-cy="export-all-financials-excel"
          >Export All To Excel</LoadingButton
        >
      </div>
    </div>
    <FinancialsGrid
      :filter="filter"
      :transactions="transactions"
      :loading="transactionsLoading"
      :skip="skip"
      :pageSize="take"
      :total="totalTransactions"
      :allowSelection="!main.isEmulator || allowEmulationPayments"
      :sort="sortDescriptors"
      :getPropValues="getPropValuesAsync"
      ref="gridRef"
      @detailsClicked="onDetailsClicked"
      @invoicesChanged="onInvoicesChanged"
      @selectionChanged="onSelectionChanged"
      @sortChanged="onSortChanged"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler :filter="filter" ref="menuIconHandlerRef" />
  </div>
</template>

<script setup lang="ts">
import ClearFiltersButton from "@/shared/ui/inputs/ClearFiltersButton.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref } from "vue";
import FinancialsGrid from "./FinancialsGrid.vue";
import type {
  FinancialTransactionWithSelection,
  GetFinancialTransactionsWithFilteringRequest,
} from "@/api/financials";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import TransactionDetailsModal from "./TransactionDetailsModal.vue";
import PaymentModal from "./PaymentModal.vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import {
  getJsonFilterString,
  valueFilterConvert,
  FilterValuesTake,
} from "@/shared/helpers/filtersHelper";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import type { FinancialTransaction } from "@/Domain/FinancialTransaction";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import { FinancialsEmulationGroup } from "@/shared/access/RoleGroups";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { saveExcel } from "@progress/kendo-vue-excel-export";
import { setDateColumnsFormat } from "@/shared/helpers/exportHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const transactions = ref<FinancialTransactionWithSelection[]>([]);
const totalTransactions = ref<number>(0);
const transactionsLoading = ref<boolean>(false);
const skip = ref<number>(0);
const take = ref<number>(100);
const selectedTransactions = ref<FinancialTransactionWithSelection[]>([]);
const paymentModalVisible = ref<boolean>(false);
const detailsModalVisible = ref<boolean>(false);
const detailsModalTransaction = ref<FinancialTransactionWithSelection>(
  {} as FinancialTransactionWithSelection
);
const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);
const exporting = ref<boolean>(false);

const tableName = "FinancialsTable";
const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);
const gridRef = ref<InstanceType<typeof FinancialsGrid> | null>(null);
const emulatorUserPaymentsAllowed = ref<boolean>(false);

const allowEmulationPayments = computed(() => {
  return (
    main.isInAnyRole(FinancialsEmulationGroup.roles) &&
    emulatorUserPaymentsAllowed.value
  );
});

const filter = ref<CompositeFilterDescriptor>({
  logic: "and",
  filters: [{} as FilterDescriptor],
});

const valueFiltersConvertOptions = [
  {
    column: "DocumentDate",
    convert: (value: string) => {
      return new Date(value).toLocaleDateString();
    },
    convertBack: (value: string) => {
      return `${value} 00:00:00`;
    },
  },
] as ValueFieldConvertOption[];


const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const getFinancialTransactionsWithFilteringAsync = async function (
  valuesForFieldOnly: string = "",
  filterObject: CompositeFilterDescriptor | null = filter.value,
  takeValue: number = take.value,
  skipValue: number = skip.value
) {
  const requestParams = {
    companyLogin: main.companyLogin,
    filters: getJsonFilterString(filterObject, valueFiltersConvertOptions),
    take: takeValue,
    skip: skipValue,
    sortField: sortField.value,
    ascending: sortAscending.value,
    valuesForFieldOnly: valuesForFieldOnly,
  } as GetFinancialTransactionsWithFilteringRequest;

  const endpoint = "GetFinancialTransactionsWithFiltering";

  if (!valuesForFieldOnly) return api.get(endpoint, requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, endpoint),
    () => api.get(endpoint, requestParams)
  );
};

const getFinancialTransactionsAsync = async function () {
  if (transactionsLoading.value) return;
  transactionsLoading.value = true;

  await handleResponsePromiseAsync(
    getFinancialTransactionsWithFilteringAsync(),
    (data) => {
      transactions.value = data.ListItems;
      transactions.value.forEach((t) => {
        t.selected = selectedTransactions.value.some(
          (st) => st.DocumentNumber === t.DocumentNumber
        );
      });
      totalTransactions.value = data.TotalItemsInList;
      console.debug(
        `FinancialsTable.vue: getFinancialTransactionsAsync(), result:`,
        data
      );
    },
    "FinancialsTable.vue",
    "getFinancialTransactionsAsync"
  );

  transactionsLoading.value = false;
};

const getPropValuesAsync = async function (column: GridColumnPropsExtended) {
  let result: FinancialTransactionWithSelection[] = [];
  await handleResponsePromiseAsync(
    getFinancialTransactionsWithFilteringAsync(column.field),
    (data) => {
      console.debug(`FinancialsTable.vue: getPropValuesAsync(), result:`, data);
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
    },
    "FinancialsTable.vue",
    "getPropValuesAsync"
  );
  return result;
};

const onInvoicesChanged = function (options: ItemChangedOptions) {
  if (options.filterEvent) {
    filter.value.filters = options.filterEvent.filter.filters;
  }
  if (take.value != options.take) {
    take.value = options.take;
    GridOptionsService.savePageSizeAsync(tableName, options.take);
  }
  skip.value = options.skip;
  getFinancialTransactionsAsync();
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  skip.value = 0;
  getFinancialTransactionsAsync();
};

const onSelectionChanged = function (
  changedTransactions: FinancialTransactionWithSelection[]
) {
  changedTransactions.forEach((transaction) => {
    const index = selectedTransactions.value.findIndex(
      (st) => st.DocumentNumber === transaction.DocumentNumber
    );
    if (transaction.selected) {
      if (index === -1) selectedTransactions.value.push(transaction);
      else
        console.debug(
          `FinancialsTable.vue: onSelectionChanged(): transaction already selected`,
          transaction,
          selectedTransactions.value[index]
        );
    } else if (index > -1) {
      selectedTransactions.value.splice(index, 1);
    } else {
      // If transaction is deselected and it is not
      // in the selectedTransactions array - do nothing
    }
  });
};

const onPaymentModalClose = function (paymentCompleted: boolean) {
  paymentModalVisible.value = false;
  if (paymentCompleted) {
    selectedTransactions.value = [];
    getFinancialTransactionsAsync();
  }
};

const onDetailsClicked = function (
  transaction: FinancialTransactionWithSelection
) {
  detailsModalTransaction.value = transaction;
  detailsModalVisible.value = true;
};

const onDeleteSelectedTransaction = function (
  transaction: FinancialTransaction
) {
  const index = selectedTransactions.value.findIndex(
    (t) => t.DocumentNumber === transaction.DocumentNumber
  );
  if (index > -1) selectedTransactions.value.splice(index, 1);
};

const clearFilters = function () {
  console.debug("FinancialsTable.vue, clearFilters()");
  filter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };
  sortField.value = "";
  sortAscending.value = false;
  getFinancialTransactionsAsync();
};

const downloadFormW9 = function () {
  return api.getDownload("GetFormW9", {});
};

const exportAllExcelAsync = async function (filtered: boolean = false) {
  try {
    exporting.value = true;
    const data = await getFinancialTransactionsWithFilteringAsync(
      "",
      filtered ? filter.value : null,
      999999,
      0
    );

    if (!data.success) {
      notificationStore.notify({
        message: "Error while exporting data. Please try again.",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
      return;
    }

    const columns = gridRef.value?.columns;
    setDateColumnsFormat(columns, data.response.ListItems);

    saveExcel({
      data: data.response.ListItems,
      fileName: "Invoices.xlsx",
      columns: columns,
    });
  } finally {
    exporting.value = false;
  }
};

const updateEmulatorPaymentsAllowedAsync = function () {
  return handleResponsePromiseAsync(
    api.get("EmulatorPaymentsAllowed", {}),
    (data) => {
      emulatorUserPaymentsAllowed.value = data;
    },
    "FinancialsTable.vue",
    "updateEmulatorPaymentsAllowedAsync"
  );
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) take.value = pageSize;
  if (main.isEmulator) updateEmulatorPaymentsAllowedAsync();
  await getFinancialTransactionsAsync();
});
</script>

<style lang="scss">
.financials-table {
  .grid-control__container {
    display: flex;
    align-items: center;
  }

  .pull-right {
    display: flex;
    margin-left: auto;
    gap: 8px;
  }
}
</style>
